import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Check, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-notifications" className="p-8">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-sm text-gray-500 mt-1">
          {notifications.filter((n) => !n.read).length} unread notifications
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-sm">
          <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No notifications yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            Notifications will appear when customer criteria match available forklifts.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <div
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`bg-white border border-gray-200 rounded-sm p-4 flex items-start gap-4 transition-colors ${
                !n.read ? "border-l-4 border-l-yellow-400" : ""
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                !n.read ? "bg-yellow-100" : "bg-gray-100"
              }`}>
                <Bell className={`w-4 h-4 ${!n.read ? "text-yellow-600" : "text-gray-400"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${!n.read ? "font-semibold text-gray-900" : "text-gray-600"}`}>
                  {n.message}
                </p>
                {n.matched_forklifts && n.matched_forklifts.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {n.matched_forklifts.map((f, i) => (
                      <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{f}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs text-gray-400">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                  </span>
                  {n.match_count && (
                    <Badge className="bg-orange-100 text-orange-700 border-none text-xs hover:bg-orange-100">
                      {n.match_count} match(es)
                    </Badge>
                  )}
                  {n.request_id && (
                    <button onClick={() => navigate(`/admin/requests/${n.request_id}`)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                      <ExternalLink className="w-3 h-3" /> View Request
                    </button>
                  )}
                  {n.crm_client_id && (
                    <button onClick={() => navigate(`/admin/crm/clients/${n.crm_client_id}`)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                      <ExternalLink className="w-3 h-3" /> View Client
                    </button>
                  )}
                </div>
              </div>
              {!n.read && (
                <button
                  data-testid={`mark-read-${n.id}`}
                  onClick={() => markRead(n.id)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-sm"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
